import slugify from "@sindresorhus/slugify";
import clsx from "clsx";
import NextImage from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { type FunctionComponent, useEffect, useState } from "react";
import { useLoadMore } from "../../../hooks/useLoadMore";
import { createFetcher } from "../../../libs/next/api/jsonApiHandler";
import type { API } from "../../../pages/api/loaders/[handler]";
import { toArray } from "../../const/ArrayHelper";
import { ellipsedText } from "../../const/EllipsedTextHelper";
import { Translation, useTranslate } from "../../contexts/TranslationContext";
import SvgChevronDown from "../../svg/icons/ChevronDown";
import style from "./BrandBanner.module.sass";
import { Button } from "./Button";
import { Hidden } from "./Hidden";

const api = createFetcher<API>("/api/loaders/[handler]");

type BrandBannerProps = {
	brandId: string;
	title: string;
	lead: string | undefined;
	noBottomBorder?: boolean;
	badge?: string;
	badgeColor?: string;
	imageUrl: string | undefined;
	buttonLabel: string;
	tagContent?: JSX.Element | undefined;
	buttonDisabled?: boolean;
	href:
		| string
		| {
				pathname: string;
				query: { [key: string]: string | string[] | undefined };
		  };
	isEmbedded?: boolean;
	isOnProductDetail?: boolean;
	showDetailButton?: boolean;
};

export const BrandBanner: FunctionComponent<BrandBannerProps> = ({
	title,
	lead,
	imageUrl,
	buttonLabel,
	tagContent,
	noBottomBorder = false,
	brandId,
	badge,
	badgeColor,
	buttonDisabled = false,
	href,
	isEmbedded = false,
	isOnProductDetail = false,
	showDetailButton = false,
}) => {
	const lastCharacterVisible = 215;
	const [isDetailVisible, setIsDetailVisible] = useState(false);
	const [search, setSearch] = useState({ flavor: [] as string[] });
	const router = useRouter();
	const translate = useTranslate();

	useEffect(() => {
		if (typeof window !== "undefined") {
			const savedState = localStorage.getItem(`detailOpenBrand_${brandId}`);
			if (savedState === "true") {
				setIsDetailVisible(true);
			}
		}
	}, [brandId]);

	useEffect(() => {
		setSearch({
			flavor: toArray(router.query.flavor),
		});
	}, [router]);

	const query = useLoadMore(["products", brandId, search], null, async (page) =>
		api("brandProductsAmount", {
			_req: { query: { page, id: brandId } },
			search,
		})
	);
	const toggleDetail = () => {
		const newState = !isDetailVisible;
		setIsDetailVisible(newState);
		if (typeof window !== "undefined") {
			localStorage.setItem(
				`detailOpenBrand_${brandId}`,
				newState ? "true" : "false"
			);
			if (newState) {
				const detailElement = document.getElementById(
					`${slugify(title)}-detail`
				);
				if (detailElement) {
					detailElement.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			}
		}
	};
	return (
		<div
			className={clsx(
				style.root,
				isEmbedded && style.rootEmbedded,
				isEmbedded && badge && style.hasBadge,
				noBottomBorder && style.noBottomBorder
			)}
		>
			<div className={style.wrapper}>
				<div className={style.article}>
					{badge && (
						<div
							className={clsx(style.badge)}
							style={{
								backgroundColor: badgeColor,
							}}
						>
							{badge}
						</div>
					)}
					<div className={style.thumbnail}>
						<div
							className={clsx(
								style.imageWrapper,
								!imageUrl && style.imageWrapperPlaceholder
							)}
						>
							<Link href={href} title={title}>
								<div className={style.image}>
									<NextImage
										src={imageUrl ?? "/images/placeholder-product.png"}
										alt={translate("alternativeText.placeholderProduct")}
										title={title}
										layout="fill"
										objectFit="contain"
									/>
								</div>
							</Link>
						</div>
					</div>
					<div className={style.body}>
						<Link href={href} title={title}>
							{isOnProductDetail ? (
								<div className={style.title}>{title}</div>
							) : (
								<h3 className={style.title}>{title}</h3>
							)}
						</Link>
						<div className={style.paragraph}>
							{lead && lead.length > lastCharacterVisible
								? ellipsedText(lead, lastCharacterVisible)
								: lead}
						</div>
					</div>
					<Hidden breakpoint="mobileUp">
						{tagContent && (
							<div className={clsx(style.article, style.articleTags)}>
								<div className={style.flavorTitle}>
									<Translation tr="brandBanner.flavors" />
								</div>
								<div className={style.tagGroup}>{tagContent}</div>
								{showDetailButton && (
									<div className={style.detailButtonWrapper}>
										<Button
											type={{ variant: "naked" }}
											label={translate("fullProduct.readMore")}
											iconAfter={
												<span
													className={clsx(
														style.icon,
														isDetailVisible && style.iconRotate
													)}
												>
													<SvgChevronDown />
												</span>
											}
											icon={{ fillPrimary: true }}
											onClick={toggleDetail}
										/>
									</div>
								)}
							</div>
						)}
					</Hidden>

					<div className={style.buttonWrapper}>
						<Button
							label={
								query.data?.pages[0]?.totalCount
									? `${translate("listingBrands.title.multiple")} (${
											query.data?.pages[0]?.totalCount
									  })`
									: buttonLabel
							}
							url={href}
							disabled={buttonDisabled}
							type={{ fullWidth: true }}
						/>
					</div>
				</div>
				<Hidden breakpoint="mobileDown" marginAuto="top">
					{tagContent && (
						<div className={clsx(style.article, style.articleTags)}>
							<div className={style.flavorTitle}>
								<Translation tr="brandBanner.flavors" />
							</div>
							<div className={style.tagGroup}>
								{tagContent}{" "}
								{showDetailButton && (
									<div className={style.detailButtonWrapper}>
										<Button
											type={{ variant: "naked" }}
											label={translate("fullProduct.readMore")}
											iconAfter={
												<span
													className={clsx(
														style.icon,
														isDetailVisible && style.iconRotate
													)}
												>
													<SvgChevronDown />
												</span>
											}
											icon={{ fillPrimary: true }}
											onClick={toggleDetail}
										/>
									</div>
								)}
							</div>

							<div className={style.tagGroup}></div>
						</div>
					)}
				</Hidden>
			</div>
		</div>
	);
};
